import { BLACK, type Color, KING, TYPE_MASK, WHITE, colorBit, parseSquare, squareName } from '../board.ts';
import { ZOMBIE_PAWN } from '../pieces.ts';
import { FenError } from '../notation.ts';
import type { MoveContext } from '../rules.ts';
import type { Position, Variant } from '../types.ts';
import { STANDARD_FEN } from './classic.ts';

// Day 11 (draft). The dead come back. Every captured piece leaves a grave. Three turns after
// you capture it (at the end of your third move), it climbs out as a zombie pawn on your
// side; if something is standing on the grave it waits, and rises at the end of the first of
// your moves that finds the square empty. A grave on your own last rank stays buried (a pawn
// there could never move). Zombie pawns are pawns in every way, and they leave graves too.
//
// ext: one slot per possible grave, each `square + 1 | captor << 7 | turns left << 8` (0 = none),
// kept sorted so equal positions hash the same.

export const GRAVE_TURNS = 3;
/**
 * Pieces on the board plus open graves never exceed the 30 non-king pieces a game starts with
 * (a capture turns a piece into a grave, a rising turns it back), so 30 slots never overflow.
 */
const MAX_GRAVES = 30;

export interface Grave {
  sq: number;
  /** The side the zombie will fight for. */
  owner: Color;
  /** The owner's moves until it rises (1 = at the end of their next move). */
  turns: number;
}

const encode = (g: Grave) => (g.sq + 1) | (g.owner << 7) | (g.turns << 8);
const decode = (e: number): Grave => ({ sq: (e & 127) - 1, owner: ((e >> 7) & 1) as Color, turns: e >> 8 });

export function graves(pos: { ext: Int32Array | null }): Grave[] {
  const out: Grave[] = [];
  for (const e of pos.ext ?? []) if (e) out.push(decode(e));
  return out;
}

function writeGraves(ext: Int32Array, list: Grave[]) {
  const codes = list.map(encode).sort((a, b) => b - a);
  for (let i = 0; i < MAX_GRAVES; i++) ext[i] = codes[i] ?? 0;
}

const lastRank = (sq: number, c: Color) => sq >> 3 === (c === WHITE ? 7 : 0);

function onlyKings(board: Int8Array): boolean {
  for (let sq = 0; sq < 64; sq++) if (board[sq] && (board[sq] & TYPE_MASK) !== KING) return false;
  return true;
}

export const zombie: Variant = {
  id: 'zombie',
  info: {
    name: 'Zombie Chess',
    but: 'the dead come back',
    tagline: 'Everything you capture climbs out of its grave three turns later. On your side.',
    summary: 'Captured pieces leave a grave. Three turns later they **rise as zombie pawns** for whoever took them.',
    rules: [
      'Every captured piece leaves a grave on the square where it was taken.',
      'At the end of your third move after the capture, it rises as a zombie pawn on your side. If something is standing on the grave, it waits until the square is empty.',
      'Zombie pawns move, capture and promote like pawns, and leave graves of their own. A grave on your own last rank stays buried.',
      'Otherwise normal chess: checkmate wins; stalemate, repetition and the 50-move rule draw.',
    ],
    accent: '#b5e655',
    emoji: '🧟',
    bots: ['Grave Digger', 'Shambler', 'Ghoul', 'Necromancer', 'Lich King'],
  },
  startFen: `${STANDARD_FEN} -`,
  extraPieces: [ZOMBIE_PAWN],
  extSize: MAX_GRAVES,
  // Any capture makes a grave that can come back as a pawn and promote, so the only dead
  // position is two bare kings with no graves left.
  insufficientMaterial: (pos) => graves(pos).length === 0 && onlyKings(pos.board),

  parseExtra(fields, pos) {
    const [field = '-'] = fields;
    const list: Grave[] = [];
    if (field !== '-') {
      for (const part of field.split(',')) {
        const m = /^([a-h][1-8])([wb])([1-9])$/.exec(part);
        if (!m) throw new FenError('Bad grave field');
        list.push({ sq: parseSquare(m[1]), owner: m[2] === 'w' ? WHITE : BLACK, turns: Number(m[3]) });
      }
    }
    if (list.length > MAX_GRAVES) throw new FenError('Too many graves');
    writeGraves(pos.ext!, list);
  },

  formatExtra(pos) {
    const list = graves(pos);
    return [list.length ? list.map((g) => `${squareName(g.sq)}${g.owner === WHITE ? 'w' : 'b'}${g.turns}`).join(',') : '-'];
  },

  afterMove(ctx: MoveContext) {
    const { next, us } = ctx;
    const ext = next.ext!;
    const list: Grave[] = [];
    for (const g of graves(next)) {
      if (g.owner !== us) list.push(g);
      else if (lastRank(g.sq, us)) continue;
      else if (g.turns > 1) list.push({ ...g, turns: g.turns - 1 });
      else if (next.board[g.sq] !== 0) list.push(g);
      else {
        ctx.place(g.sq, ZOMBIE_PAWN | colorBit(us), 'zombie');
        ctx.fx('rise', g.sq);
      }
    }
    if (ctx.captured && ctx.capturedSq >= 0 && !lastRank(ctx.capturedSq, us)) list.push({ sq: ctx.capturedSq, owner: us, turns: GRAVE_TURNS });
    writeGraves(ext, list);
  },
};

/** Squares where a zombie rises right after `c`'s next move. */
export function risingNext(pos: Position, c: Color): number[] {
  return graves(pos)
    .filter((g) => g.owner === c && g.turns === 1 && pos.board[g.sq] === 0 && !lastRank(g.sq, c))
    .map((g) => g.sq);
}
